import { Component, ViewChild } from '@angular/core';
import { AppService } from '../../../../services/app.service';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { ProductionService } from '../../../../services/productions/production.service';
import { ProductionEnvelope } from '../../../../models/net/envelope/production-envelope';
import { ProductionType } from '../../../../models/data/talent-core/production-type';
import { EditorComponent } from '../../../base/editor/editor.component';
import { ProductionDocument } from '../../../../models/net/envelope/documents/production-document';
import { ProductionRole } from '../../../../models/data/talent-core/production-role';
import { MatTableDataSource } from '@angular/material/table';
import { Production } from '../../../../models/data/talent-core/production';
import { ProductionStatus } from '../../../../models/data/talent-core/production-status';
import { HistoryComponent } from '../../../fragments/history/history.component';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ProductionResource } from '../../../../models/data/talent-core/production-resource';
import { AvatarPrimitive } from '../../../../models/app/avatar-primitive';
import { ResourceSearchResult } from '../../../../models/net/result/resource-search-result';
import { ResourceType } from '../../../../models/data/talent-core/resource-type';
import { ChipSelectorComponent } from '../../../fragments/chip-selector/chip-selector.component';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { EngagementSearchResult } from '../../../../models/net/result/engagement-search-result';
import { EngagementService } from '../../../../services/engagements/engagement.service';
import { ItemEnvelope } from '../../../../models/net/envelope/base/item-envelope';
import { EngagementEnvelope } from '../../../../models/net/envelope/engagement-envelope';
import { Engagement } from '../../../../models/data/talent-core/engagement';
import { EnvelopeFactory } from '../../../../utilities/envelope-factory';
import { EngagementTileComponent } from '../../../fragments/talent-core/engagement-tile/engagement-tile.component';
import { ComponentDialogResult } from '../../../../models/app/component-dialog-result';
import { DialogableEngagementEnvelope } from '../../../../models/app/dialogable-engagement-envelope';
import { EngagementStatus } from '../../../../models/data/talent-core/engagement-status';
import { MessageSeverity } from '../../../../enums/message-severity';

@Component({
    selector: 'app-production',
    templateUrl: './production.component.html',
    styleUrls: ['./production.component.css', '../../../../app.component.css']
})

export class ProductionComponent extends EditorComponent {

    public proformaEngagementEnvelope: EngagementEnvelope | undefined;
    public productionRolesCache: ProductionRole[] = [];
    public avatarUrl: string = "";
    public enteredRoleName: string = "";
    public enteredRoleDescription: string = "";
    public displayedProductionRoleColumns: string[] = ["RoleName", "RoleDescription", "Delete"];
    public productionRolesDatasource = new MatTableDataSource((this.itemEnvelope?.Document as ProductionDocument).Roles);
    public displayedProductionResourceColumns: string[] = ["Avatar", "ResourceName", "ResourceType", "Delete"];
    public availableResources: AvatarPrimitive[] = [];
    public selectedResourceIds: number[] = [];
    public selectedResourceTypeId?: number;
    public productionResourcesDatasource = new MatTableDataSource((this.itemEnvelope?.Document as ProductionDocument).Resources);
    public productionStatuses: ProductionStatus[] = [];
    public resourceTypes: ResourceType[] = [];
    public selectedProductionStatusId?: number;
    public engagementsDatasource = new MatTableDataSource((this.itemEnvelope?.Document as ProductionDocument).Engagements);
    public displayedEngagementsColumns = ["Avatar", "ResourceName", "RoleName", "EngagementStatus", "StartOnUTC", "Delete"];
    public dynamicClassNameRoleSpan = "expanding-span-collapsed";
    public dynamicClassNameStaffSpan = "expanding-span-collapsed";

    @ViewChild("appHistory") appHistory!: HistoryComponent;
    @ViewChild("resourceChipSelector") resourceChipSelector!: ChipSelectorComponent;
    @ViewChild("resourceTypesSelector") resourceTypesSelector!: MatSelect;

    public constructor(public override appService: AppService,
                       public override itemService: ProductionService,
                       public override route: ActivatedRoute,
                       public override location: Location,
                       public engagementService: EngagementService) {

        super(appService, itemService, route, location, "production", "productions");
    }

    public override ngOnInit(): void {

        super.ngOnInit();

        this.engagementService.getBlankEnvelope().subscribe((r: ItemEnvelope | null) => {

            if (r) {

                this.proformaEngagementEnvelope = r as EngagementEnvelope;
            }
        });
    }

    public ngAfterViewInit(): void {

        this.resourceChipSelector.itemAdded.subscribe((item: AvatarPrimitive) => {

            this.selectedResourceIds.push(item.Id);
        });

        this.resourceChipSelector.itemRemoved.subscribe((item: AvatarPrimitive) => {

            this.selectedResourceIds = [];
        });
    }

    public override initializeEnvelope = (envelope: ProductionEnvelope | null): void => {

        super.initializeEnvelope(envelope);

        const productionRoleIds: number[] = envelope?.Document.Engagements.map((i: Engagement) => i.ProductionRoleId ?? 0) ?? [0];

        this.itemService.getProductionRolesByIds(productionRoleIds).subscribe((r: ProductionRole[] | null) => {

            if (r) {

                this.productionRolesCache = r;

                this.itemEnvelope = new ProductionEnvelope(envelope);
                this.itemEnvelopeSnapshot = this.appService.cloneObject(this.itemEnvelope);
                this.productionRolesDatasource = new MatTableDataSource((this.itemEnvelope?.Document as ProductionDocument).Roles);
                this.productionResourcesDatasource = new MatTableDataSource((this.itemEnvelope?.Document as ProductionDocument).Resources);
                this.productionStatuses = (this.itemEnvelope as ProductionEnvelope).ProductionStatuses;
                this.resourceTypes = (this.itemEnvelope as ProductionEnvelope).ResourceTypes;
                this.engagementsDatasource = new MatTableDataSource((this.itemEnvelope?.Document as ProductionDocument).Engagements);

                const availableResources: AvatarPrimitive[] = (this.itemEnvelope as ProductionEnvelope).Resources.map((i: ResourceSearchResult) => {
                    let result = new AvatarPrimitive();
                    result.Id = i.ResourceId;
                    result.Name = i.FirstName + " " + i.LastName;
                    result.AvatarBlobPath = i.AvatarBlobPath;
                    return result;
                });

                this.availableResources = availableResources;
            }
        });
    }

    public getProductionTypeUIDFromProductionTypeId = (productionTypeId: number): string => {

        let result: string = "";
        let productionType: ProductionType | undefined = (<ProductionEnvelope>this.itemEnvelope)?.ProductionTypes.find((i: ProductionType) => i.ProductionTypeId == productionTypeId);

        if (productionType) {

            result = productionType.UID;
        }

        return result;
    }

    public addRoleToProduction = (): void => {

        const newProductionRole = new ProductionRole();

        if (this.dynamicClassNameRoleSpan == "expanding-span-collapsed") {

            this.dynamicClassNameRoleSpan = "expanding-span-expanded";

        } else {

            newProductionRole.ProductionId = (this.itemEnvelope?.Document?.Item as Production).ProductionId;
            newProductionRole.Name = this.enteredRoleName;
            newProductionRole.Description = this.enteredRoleDescription;

            (this.itemEnvelope?.Document as ProductionDocument).Roles.push(newProductionRole);
            this.productionRolesDatasource = new MatTableDataSource((this.itemEnvelope?.Document as ProductionDocument).Roles);

            this.enteredRoleName = "";
            this.enteredRoleDescription = "";
            this.dynamicClassNameRoleSpan = "expanding-span-collapsed";
        }
    }

    public cancelAddRoleToProduction = (): void => {

        this.dynamicClassNameRoleSpan = "expanding-span-collapsed";
        this.enteredRoleName = "";
        this.enteredRoleDescription = "";
    }

    public deleteProductionRole = (productionRole: ProductionRole): void => {

        const itemIndex: number | undefined = (this.itemEnvelope?.Document as ProductionDocument)?.Roles.map((i: ProductionRole) => i.Name).indexOf(productionRole.Name, 0);

        if (itemIndex != undefined && itemIndex > -1) {

            (this.itemEnvelope?.Document as ProductionDocument)?.Roles.splice(itemIndex, 1);
            this.productionRolesDatasource = new MatTableDataSource((this.itemEnvelope?.Document as ProductionDocument).Roles);
        }
    }

    public onTabChange(event: MatTabChangeEvent) {

        if (event.index == 1 && !this.isRevisionListLoaded) {

            this.appHistory.searchRevisions();
        }
    }

    public addResourceToProduction = (): void => {

        if (this.dynamicClassNameStaffSpan == "expanding-span-collapsed") {

            this.dynamicClassNameStaffSpan = "expanding-span-expanded";

        } else {

            const document: ProductionDocument = (this.itemEnvelope?.Document as ProductionDocument);
            const newProductionResource: ProductionResource = new ProductionResource();

            newProductionResource.ProductionId = (this.itemEnvelope?.Document as ProductionDocument).Item.ProductionId;
            newProductionResource.ResourceId = this.selectedResourceIds[0];

            if (this.selectedResourceTypeId) {

                newProductionResource.ResourceTypeId = this.selectedResourceTypeId;
            }

            document.Resources.push(newProductionResource);
            this.productionResourcesDatasource = new MatTableDataSource(document.Resources);

            this.resourceChipSelector.clear();
            this.resourceTypesSelector.value = null;
            this.selectedResourceIds = [];
            this.selectedResourceTypeId = undefined;

            if (this.resourceChipSelector.selectedItems.length > 0) {

                this.resourceChipSelector.remove(this.resourceChipSelector.selectedItems[0]);
            }

            this.dynamicClassNameStaffSpan = "expanding-span-collapsed"
        }
    }

    public onResourceTypeChange = (event: MatSelectChange): void => {

        this.selectedResourceTypeId = event.value as number;
    }

    public cancelAddResourceToProduction = (): void => {

        this.dynamicClassNameStaffSpan = "expanding-span-collapsed";

        this.resourceChipSelector.clear();
        this.resourceTypesSelector.value = null;
        this.selectedResourceIds = [];
        this.selectedResourceTypeId = undefined;

        if (this.resourceChipSelector.selectedItems.length > 0) {

            this.resourceChipSelector.remove(this.resourceChipSelector.selectedItems[0]);
        }
    }

    public deleteProductionResource = (productionResource: ProductionResource): void => {

        const itemIndex: number | undefined = (this.itemEnvelope?.Document as ProductionDocument)?.Resources.map((i: ProductionResource) => i.ProductionResourceId).indexOf(productionResource.ProductionResourceId, 0);

        if (itemIndex != undefined && itemIndex > -1) {

            (this.itemEnvelope?.Document as ProductionDocument)?.Resources.splice(itemIndex, 1);
            this.productionResourcesDatasource = new MatTableDataSource((this.itemEnvelope?.Document as ProductionDocument).Resources);
        }
    }

    public getResourceFullNameFromResourceId = (resourceId: number): string => {

        let result: string = "";

        let resource: ResourceSearchResult | undefined = (this.itemEnvelope as ProductionEnvelope)?.Resources.find((i: ResourceSearchResult) => i.ResourceId == resourceId);

        if (resource) {

            result = resource.FirstName + " " + resource.LastName;
        }

        return result;
    }

    public getResourceTypeNameFromProductionTypeId = (resourceTypeId: number): string => {

        let result: string = "";

        let resourceType: ResourceType | undefined = (this.itemEnvelope as ProductionEnvelope)?.ResourceTypes.find((i: ResourceType) => i.ResourceTypeId == resourceTypeId);

        if (resourceType) {

            result = resourceType.UID;
        }

        return result;
    }

    public getAvatarBlobPathFromResourceId = (resourceId: number): string | undefined => {

        let result: string | undefined = "";

        let resource: ResourceSearchResult | undefined = (this.itemEnvelope as ProductionEnvelope)?.Resources.find((i: ResourceSearchResult) => i.ResourceId == resourceId);

        if (resource) {

            result = resource.AvatarBlobPath;
        }

        return result;
    }

    public editEngagement = (engagement: Engagement): void => {

        this.engagementService.getBlankEnvelope().subscribe((r: ItemEnvelope | null) => { // need to go back to the database mainly for the engagement envelope lookup values

            if (r) {

                const proformaEngagementEnvelope: EngagementEnvelope = r as EngagementEnvelope;

                if (engagement) { // this is the data source for the engagement that appears in the list of engagements

                    const engagementEnvelope: EngagementEnvelope = EnvelopeFactory.assembleEngagementEnvelope(engagement, proformaEngagementEnvelope, this.appService);
                    let dialogableEngagementEnvelope: DialogableEngagementEnvelope = Object.assign(new DialogableEngagementEnvelope(null), engagementEnvelope);

                    dialogableEngagementEnvelope.isProductionChipSelectorDisabled = true;

                    this.appService.showComponentDialog(EngagementTileComponent, dialogableEngagementEnvelope, "Edit Engagement: " + (this.itemEnvelope as ProductionEnvelope).Document.Item.Name, "", "cinematic_blur", undefined, undefined, false).subscribe((r: ComponentDialogResult | null) => {

                        if (r) {

                            if (r.IsAccepted) { // this means the user made a change and hit ok.  put the changes back into the item envelope

                                const productionRoleId: number | undefined = (r.DataSource as EngagementEnvelope).Document.Item.ProductionRoleId;
                                const isMissingProductionRoleLookup: boolean = !this.productionRolesCache.some((i: ProductionRole) => i.ProductionRoleId == productionRoleId);

                                // need to find out if the productionroleid (if any) is in the list of productionroles which are used by the grid to display names instead of numbers
                                if (productionRoleId && isMissingProductionRoleLookup) {

                                    this.itemService.getProductionRolesByIds([productionRoleId]).subscribe((productionRoles: ProductionRole[] | null) => {

                                        if (productionRoles) {

                                            this.productionRolesCache = this.productionRolesCache.concat(productionRoles); // build the look up list 
                                            this.itemEnvelope = EnvelopeFactory.mergeEngagementoToProductionEnvelope(r.DataSource, (this.itemEnvelope as ProductionEnvelope));
                                            this.engagementsDatasource = new MatTableDataSource((this.itemEnvelope?.Document as ProductionDocument).Engagements);
                                        }
                                    });

                                } else { // no need to buid up the production role lookup list

                                    this.itemEnvelope = EnvelopeFactory.mergeEngagementoToProductionEnvelope(r.DataSource, (this.itemEnvelope as ProductionEnvelope));
                                    this.engagementsDatasource = new MatTableDataSource((this.itemEnvelope?.Document as ProductionDocument).Engagements);
                                }
                            }
                        }
                    });
                }
            }
        });
    }


    public addEngagement = (): void => {

        this.engagementService.getBlankEnvelope().subscribe((r: ItemEnvelope | null) => { // need to go back to the database mainly for the engagement envelope lookup values

            if (r) {

                const proformaEngagementEnvelope: EngagementEnvelope = r as EngagementEnvelope;
                const engagement: Engagement = new Engagement();

                engagement.EngagementId = EnvelopeFactory.getProformaEngagementId((this.itemEnvelope as ProductionEnvelope).Document.Engagements);
                engagement.ProductionId = (this.itemEnvelope?.Document?.Item as Production).ProductionId

                if (engagement) { // this is the data source for the engagement that appears in the list of engagements

                    const engagementEnvelope: EngagementEnvelope = EnvelopeFactory.assembleEngagementEnvelope(engagement, proformaEngagementEnvelope, this.appService);
                    let dialogableEngagementEnvelope: DialogableEngagementEnvelope = Object.assign(new DialogableEngagementEnvelope(null), engagementEnvelope);
                    const dialogTitle: string = "New Engagement: " + (this.itemEnvelope as ProductionEnvelope).Document.Item.Name;
                    const materialIconName: string = "cinematic_blur";

                    dialogableEngagementEnvelope.isProductionChipSelectorDisabled = true;

                    this.appService.showComponentDialog(EngagementTileComponent, dialogableEngagementEnvelope, dialogTitle, "", materialIconName, undefined, undefined, false).subscribe((r: ComponentDialogResult | null) => {

                        if (r) {

                            if (r.IsAccepted) { // this means the user made a change and hit ok.  put the changes back into the item envelope

                                const productionRoleId: number | undefined = (r.DataSource as EngagementEnvelope).Document.Item.ProductionRoleId;
                                const isMissingProductionRoleLookup: boolean = !this.productionRolesCache.some((i: ProductionRole) => i.ProductionRoleId == productionRoleId);

                                // need to find out if the productionroleid (if any) is in the list of productionroles which are used by the grid to display names instead of numbers
                                if (productionRoleId && isMissingProductionRoleLookup) {

                                    this.itemService.getProductionRolesByIds([productionRoleId]).subscribe((productionRoles: ProductionRole[] | null) => {

                                        if (productionRoles) {

                                            this.productionRolesCache = this.productionRolesCache.concat(productionRoles); // build the look up list 
                                            this.itemEnvelope = EnvelopeFactory.mergeEngagementoToProductionEnvelope(r.DataSource, (this.itemEnvelope as ProductionEnvelope));
                                            this.engagementsDatasource = new MatTableDataSource((this.itemEnvelope?.Document as ProductionDocument).Engagements);
                                        }
                                    });

                                } else { // merge without roles

                                    this.itemEnvelope = EnvelopeFactory.mergeEngagementoToProductionEnvelope(r.DataSource, (this.itemEnvelope as ProductionEnvelope));
                                    this.engagementsDatasource = new MatTableDataSource((this.itemEnvelope?.Document as ProductionDocument).Engagements);
                                }
                            }
                        }
                    });
                }
            }
        });
    }

    public deleteEngagement = (engagement: Engagement): void => {

        const confirmResponse: string = "YES, I AM SURE";

        this.appService.showDialog(MessageSeverity.Warning, "Confirm Deletion", "Are you sure you want to delete the engagement for  " + (this.itemEnvelope?.Document as ProductionDocument).Item.Name + " ?", confirmResponse, "NO, NOT SURE", false, false).subscribe((r: string) => {

            if (r == confirmResponse) {

                this.itemEnvelope = EnvelopeFactory.removeEngagementFromProduction(engagement, (this.itemEnvelope as ProductionEnvelope), this.appService);
                this.engagementsDatasource = new MatTableDataSource((this.itemEnvelope?.Document as ProductionDocument).Engagements);
            }
        });
    }

    public getResourceNameFromEngagement = (engagement: Engagement): string => {

        let result: string = "";

        let resource: ResourceSearchResult | undefined = this.proformaEngagementEnvelope?.AvailableResources.find((i: ResourceSearchResult) => i.ResourceId == engagement.ResourceId);

        if (resource) {

            result = resource.FirstName + " " + resource.LastName;
        }

        return result;
    }

    public getResourceAvatarBlobPathFromEngagement = (engagement: Engagement): string => {

        let result: string = this.proformaEngagementEnvelope?.AvailableResources.find((i: ResourceSearchResult) => i.ResourceId == engagement.ResourceId)?.AvatarBlobPath ?? this.appService.defaultImageUri;

        return result;
    }

    public getProductionRoleNameFromEngagement = (engagement: Engagement): string => {

        let result: string = this.productionRolesCache.find((i: ProductionRole) => i.ProductionRoleId == engagement.ProductionRoleId)?.Name ?? "";

        return result;
    }

    public getEngagementStatusNameFromEngagement = (engagement: Engagement): string => {

        let result: string = this.proformaEngagementEnvelope?.EngagementStatuses.find((i: EngagementStatus) => i.EngagementStatusId == engagement.EngagementStatusId)?.UID ?? "";

        return result;
    }
}
